import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, CreditCard, EyeOff, FileText, Save, Shield, UserRound, Zap } from 'lucide-react';
import { API_BASE_URL } from '../config/api';
import SiteHeader from '../components/SiteHeader';
import Footer from '../components/Footer';
import useAuthModal from '../hooks/useAuthModal';
import { clearAuthSession, getAuthToken } from '../utils/auth';
import { trackEvent } from '../utils/amplitude';
import SubscriptionLimitModal from '../components/SubscriptionLimitModal';
import PrivacyDataSection from '../components/PrivacyDataSection';
import { openBillingPortal, getDashboardUsage } from '../utils/dashboardApi';
import { getCadenceLabel, inferIsPaidPlan, normalizePlanCode, resolvePlanDefinition } from './profilePlanState';

const LANGUAGE_OPTIONS = [
  'English',
  'Russian',
  'Spanish',
  'German',
  'French',
  'Italian',
  'Polish',
  'Ukrainian',
  'Czech',
  'Slovak',
];

const COACH_MODES = [
  { id: 'friend', title: 'Friend', symbol: 'FR', description: 'Warm and encouraging. Highlights strengths and gently points to gaps.' },
  { id: 'guide', title: 'Guide', symbol: 'GU', description: 'Balanced feedback. Explains reasoning and teaches through questions.' },
  { id: 'challenger', title: 'Challenger', symbol: 'CH', description: 'Pushes harder. Expects precision and points to weak arguments directly.' },
  { id: 'drill_sergeant', title: 'Drill Sergeant', symbol: 'DS', description: 'No mercy. Stress-tests every answer like a tough real interview.' },
];

const COACH_MODE_CAPABILITY = {
  friend: 'allow_coach_friend',
  guide: 'allow_coach_guide',
  challenger: 'allow_coach_challenger',
  drill_sergeant: 'allow_coach_sergeant',
};

const TOPIC_ITEMS = [
  { key: 'allow_topic_soft_skills', label: 'Soft Skills' },
  { key: 'allow_topic_hard_skills', label: 'Hard Skills' },
  { key: 'allow_topic_behavioral', label: 'Behavioral' },
  { key: 'allow_topic_motivation', label: 'Motivation' },
  { key: 'allow_topic_ai', label: 'AI Mix' },
];

const SUMMARY_DEPTH_LEVELS = ['low', 'medium', 'high'];
const ANON_ANIMALS = ['Fox', 'Owl', 'Wolf', 'Bear', 'Lynx', 'Hawk', 'Puma', 'Orca', 'Raven', 'Tiger'];
const ANONYMOUS_MODE_KEY = 'profile_anonymous_mode';

const DEFAULT_CAPABILITIES = {
  plan_code: null,
  plan_name: null,
  plan_price_usd: null,
  is_paid_plan: null,
  auto_renew: null,
  expires_at: null,
  next_billing_date: null,
  summary_level: 'medium',
  allow_interview_quick: true,
  allow_interview_standard: true,
  allow_interview_full: true,
  allow_topic_soft_skills: true,
  allow_topic_hard_skills: true,
  allow_topic_motivation: true,
  allow_topic_behavioral: true,
  allow_topic_ai: true,
  allow_top_skills: true,
  allow_skills_in_progress: true,
  allow_coach_friend: true,
  allow_coach_guide: true,
  allow_coach_challenger: true,
  allow_coach_sergeant: true,
  allow_bars_scoring: true,
};

function useMediaQuery(query) {
  const [matches, setMatches] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const media = window.matchMedia(query);
    const handler = (event) => setMatches(event.matches);
    setMatches(media.matches);
    media.addEventListener('change', handler);
    return () => media.removeEventListener('change', handler);
  }, [query]);

  return matches;
}

function normalizeProfile(data) {
  return {
    first_name: data?.first_name ?? '',
    last_name: data?.last_name ?? '',
    email: data?.email ?? '',
    language: data?.language ?? 'English',
    resume_text: data?.resume_text ?? '',
    top_skills: data?.top_skills ?? '',
    skills_in_progress: data?.skills_in_progress ?? '',
    coach_mode: data?.coach_mode ?? 'guide',
  };
}

function formatPlanPrice(priceUsd) {
  const amount = Number(priceUsd);
  if (!Number.isFinite(amount)) return null;
  const isInteger = Number.isInteger(amount);
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: isInteger ? 0 : 2,
    maximumFractionDigits: isInteger ? 0 : 2,
  }).format(amount);
}

function formatBillingDate(value) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  }).format(date);
}

export default function ProfileScreen() {
  const navigate = useNavigate();
  const authModal = useAuthModal();
  const isDesktop = useMediaQuery('(min-width: 1024px)');

  useEffect(() => { trackEvent('profile'); }, []);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [capabilities, setCapabilities] = useState(DEFAULT_CAPABILITIES);
  const [usageData, setUsageData] = useState(null);
  const [showLimitModal, setShowLimitModal] = useState(false);
  const [cancellingRenewal, setCancellingRenewal] = useState(false);
  const [limitErrorMessage, setLimitErrorMessage] = useState('');

  const [anonymousMode, setAnonymousMode] = useState(false);

  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    language: 'English',
    resume_text: '',
    top_skills: '',
    skills_in_progress: '',
    coach_mode: 'guide',
  });

  useEffect(() => {
    try {
      setAnonymousMode(localStorage.getItem(ANONYMOUS_MODE_KEY) === 'true');
    } catch {
      setAnonymousMode(false);
    }
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(ANONYMOUS_MODE_KEY, String(anonymousMode));
    } catch {
      // ignore storage errors
    }
  }, [anonymousMode]);

  const loadUsageData = useCallback(async () => {
    try {
      setUsageData(await getDashboardUsage().catch(() => null));
    } catch (loadError) {
      console.error('Failed to load dashboard usage:', loadError);
    }
  }, []);

  const loadProfile = useCallback(async () => {
    const token = getAuthToken();
    if (!token) {
      setError('Session expired. Please log in again.');
      setLoading(false);
      navigate('/', { replace: true, state: { openAuth: true, action: 'profile', redirectTo: '/profile' } });
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/profile`, {
        method: 'GET',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });

      if (response.status === 401) {
        clearAuthSession();
        navigate('/', { replace: true, state: { openAuth: true, action: 'profile', redirectTo: '/profile' } });
        return;
      }

      const data = await response.json();
      if (!response.ok) {
        setError(data.detail || data.message || 'Failed to load profile');
        setLoading(false);
        return;
      }

      setFormData(normalizeProfile(data));
      if (data?.capabilities) setCapabilities((prev) => ({ ...prev, ...data.capabilities }));
    } catch (loadError) {
      setError('An error occurred while loading profile');
      console.error('Load profile error:', loadError);
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    loadProfile();
    loadUsageData();
  }, [loadProfile, loadUsageData]);

  useEffect(() => {
    const modeIsLocked = (modeId) => !capabilities?.[COACH_MODE_CAPABILITY[modeId]];
    setFormData((prev) => {
      if (!prev.coach_mode || !modeIsLocked(prev.coach_mode)) return prev;
      const fallback = COACH_MODES.find((mode) => !modeIsLocked(mode.id))?.id || 'guide';
      return { ...prev, coach_mode: fallback };
    });
  }, [capabilities]);

  const saveProfile = async () => {
    const token = getAuthToken();
    if (!token) {
      setError('Session expired. Please log in again.');
      navigate('/', { replace: true, state: { openAuth: true, action: 'profile', redirectTo: '/profile' } });
      return;
    }

    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/profile`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(normalizeProfile(formData)),
      });

      if (response.status === 401) {
        clearAuthSession();
        navigate('/', { replace: true, state: { openAuth: true, action: 'profile', redirectTo: '/profile' } });
        return;
      }

      if (response.status === 402) {
        const data = await response.json().catch(() => ({}));
        setLimitErrorMessage(data.detail || 'This feature is not available on your current plan.');
        setShowLimitModal(true);
        setSaving(false);
        return;
      }

      const data = await response.json();
      if (!response.ok) {
        setError(data.detail || data.message || 'Failed to save profile');
        setSaving(false);
        return;
      }

      setSuccess('Profile updated successfully!');
      setFormData(normalizeProfile(data));
      if (data?.capabilities) setCapabilities((prev) => ({ ...prev, ...data.capabilities }));
      if (formData.resume_text?.trim()) trackEvent('profile_cv');
      if (formData.top_skills?.trim()) trackEvent('profile_top');
    } catch (saveError) {
      setError('An error occurred while saving profile');
      console.error('Save profile error:', saveError);
    } finally {
      setSaving(false);
    }
  };

  const handleCancelAutoRenewal = useCallback(async () => {
    if (cancellingRenewal) return;
    setCancellingRenewal(true);
    setError('');
    setSuccess('');
    try {
      const url = await openBillingPortal();
      window.location.href = url;
    } catch (cancelError) {
      setError(cancelError.message || 'Failed to open billing portal.');
      console.error('Open billing portal error:', cancelError);
      setCancellingRenewal(false);
    }
  }, [cancellingRenewal]);

  const handleChange = (event) => setFormData((prev) => ({ ...prev, [event.target.name]: event.target.value }));

  const isTopSkillsLocked = !capabilities?.allow_top_skills;
  const isSkillsInProgressLocked = !capabilities?.allow_skills_in_progress;
  const isCoachModeLocked = (modeId) => !capabilities?.[COACH_MODE_CAPABILITY[modeId]];

  const fullName = `${formData.first_name || ''} ${formData.last_name || ''}`.trim() || 'Your Name';
  const initials = useMemo(() => {
    const first = formData.first_name?.[0] || '';
    const last = formData.last_name?.[0] || '';
    if (first || last) return `${first}${last}`.toUpperCase();
    return (formData.email || 'FN').slice(0, 2).toUpperCase();
  }, [formData.first_name, formData.last_name, formData.email]);

  const anonymousAnimal = useMemo(() => ANON_ANIMALS[Math.floor(Math.random() * ANON_ANIMALS.length)], []);
  const displayName = anonymousMode ? `Anonymous-${anonymousAnimal}` : fullName;
  const displayEmail = anonymousMode ? '****@****.**' : (formData.email || 'No email yet');
  const normalizedPlanCode = useMemo(() => normalizePlanCode(capabilities?.plan_code), [capabilities?.plan_code]);

  const currentPlan = useMemo(() => resolvePlanDefinition(normalizedPlanCode), [normalizedPlanCode]);
  const isPaidPlan = useMemo(() => inferIsPaidPlan(capabilities), [capabilities]);

  const resolvedPlanPrice = useMemo(() => {
    const priceFromApi = Number(capabilities?.plan_price_usd);
    if (Number.isFinite(priceFromApi)) return priceFromApi;
    return Number(currentPlan.fallbackPriceUsd || 0);
  }, [capabilities?.plan_price_usd, currentPlan]);

  const planPriceLabel = useMemo(() => formatPlanPrice(resolvedPlanPrice), [resolvedPlanPrice]);
  const cadenceLabel = useMemo(
    () => getCadenceLabel(normalizedPlanCode || currentPlan.planCodes[0]),
    [normalizedPlanCode, currentPlan]
  );
  const renewsOnLabel = useMemo(
    () => formatBillingDate(capabilities?.next_billing_date || capabilities?.expires_at),
    [capabilities?.next_billing_date, capabilities?.expires_at]
  );
  const accessUntilLabel = useMemo(
    () => formatBillingDate(capabilities?.expires_at || capabilities?.next_billing_date),
    [capabilities?.expires_at, capabilities?.next_billing_date]
  );

  const difficultyLevels = useMemo(() => {
    if (usageData?.difficulty_levels?.length) return usageData.difficulty_levels;
    return [
      { name: 'Quick', available: Boolean(capabilities.allow_interview_quick) },
      { name: 'Medium', available: Boolean(capabilities.allow_interview_standard) },
      { name: 'Hard', available: Boolean(capabilities.allow_interview_full) },
    ];
  }, [usageData, capabilities]);

  const coachModeAvailability = useMemo(() => {
    if (usageData?.coach_modes?.length) return usageData.coach_modes;
    return COACH_MODES.map((mode) => ({ id: mode.id, name: mode.title, available: Boolean(capabilities?.[COACH_MODE_CAPABILITY[mode.id]]) }));
  }, [usageData, capabilities]);

  const topicCapabilities = useMemo(() => TOPIC_ITEMS.map((topic) => ({ ...topic, available: Boolean(capabilities?.[topic.key]) })), [capabilities]);
  const summaryLevel = String(capabilities?.summary_level || 'medium').toLowerCase();

  return (
    <div className="min-h-screen bg-[#F6F7FB] text-[#1E293B]">
      <SiteHeader authModal={authModal} />

      <main className="mx-auto w-full max-w-6xl space-y-6 px-4 py-6 sm:px-6 sm:py-8">
        {error ? <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">{error}</div> : null}
        {success ? <div className="rounded-2xl border border-green-200 bg-green-50 p-4 text-sm text-green-700">{success}</div> : null}

        <section>
          <h1 className="text-2xl font-semibold text-slate-900 sm:text-3xl">Profile & Settings</h1>
          <p className="mt-1 text-sm text-gray-500">Manage your account, skills and AI coach preferences.</p>
        </section>

        <section className={`grid items-start gap-5 ${isDesktop ? 'lg:grid-cols-[1fr_340px]' : 'grid-cols-1'}`}>
          <div className="space-y-5">
            <article className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm sm:p-6">
              <div className="mb-5 flex items-center gap-4">
                <div className={`flex h-14 w-14 items-center justify-center rounded-xl text-xl font-bold text-white ${anonymousMode ? 'bg-slate-700' : 'bg-orange-500'}`}>
                  {anonymousMode ? <EyeOff className="h-6 w-6" /> : initials}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-xl font-semibold text-slate-900">{loading ? 'Loading...' : displayName}</p>
                  <p className="truncate text-sm text-gray-500">{displayEmail}</p>
                </div>
                <button
                  type="button"
                  onClick={() => setAnonymousMode((prev) => !prev)}
                  className={`relative h-7 w-12 rounded-full transition ${anonymousMode ? 'bg-orange-500' : 'bg-gray-300'}`}
                  aria-pressed={anonymousMode}
                >
                  <span className={`absolute top-1 h-5 w-5 rounded-full bg-white shadow transition ${anonymousMode ? 'left-6' : 'left-1'}`} />
                </button>
              </div>

              {anonymousMode ? (
                <div className="mb-4 flex items-start gap-2 rounded-xl border border-orange-200 bg-orange-50 p-3 text-xs text-orange-700">
                  <Shield className="mt-0.5 h-4 w-4 flex-shrink-0" />
                  <p>Anonymous mode is on. Your real name and email are hidden during interviews.</p>
                </div>
              ) : null}

              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">First name</label>
                  <input
                    type="text"
                    name="first_name"
                    value={anonymousMode ? 'Anonymous' : formData.first_name}
                    onChange={handleChange}
                    disabled={saving || loading || anonymousMode}
                    className="w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm text-slate-800 disabled:bg-gray-100 disabled:text-gray-500"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">Last name</label>
                  <input
                    type="text"
                    name="last_name"
                    value={anonymousMode ? anonymousAnimal : formData.last_name}
                    onChange={handleChange}
                    disabled={saving || loading || anonymousMode}
                    className="w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm text-slate-800 disabled:bg-gray-100 disabled:text-gray-500"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">Email</label>
                  <input type="email" value={displayEmail} disabled className="w-full rounded-xl border border-gray-200 bg-gray-100 px-3 py-2.5 text-sm text-gray-500" />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">Interview language</label>
                  <select
                    name="language"
                    value={formData.language}
                    onChange={handleChange}
                    disabled={saving || loading}
                    className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm text-slate-800 disabled:bg-gray-100"
                  >
                    {LANGUAGE_OPTIONS.map((language) => <option key={language} value={language}>{language}</option>)}
                  </select>
                </div>
              </div>
            </article>

            <article className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm sm:p-6">
              <div className="mb-3 flex items-center gap-2">
                <FileText className="h-4 w-4 text-blue-500" />
                <h3 className="text-base font-semibold text-slate-900">Resume</h3>
              </div>
              <textarea
                rows={5}
                name="resume_text"
                value={formData.resume_text}
                onChange={handleChange}
                disabled={saving || loading}
                className="w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm text-slate-800"
              />
            </article>

            <article className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm sm:p-6">
              <div className="mb-4 flex items-center gap-2">
                <Zap className="h-4 w-4 text-amber-500" />
                <h3 className="text-base font-semibold text-slate-900">Skills & Focus Areas</h3>
              </div>

              <div className="space-y-3">
                <div className="rounded-xl border border-amber-200 bg-amber-50 p-3">
                  <p className="text-sm font-semibold text-slate-900">My Top Skills</p>
                  <textarea
                    rows={3}
                    name="top_skills"
                    value={formData.top_skills}
                    onChange={handleChange}
                    disabled={saving || isTopSkillsLocked}
                    className={`mt-2 w-full rounded-xl border px-3 py-2.5 text-sm ${isTopSkillsLocked ? 'cursor-not-allowed border-gray-200 bg-gray-100 text-gray-500' : 'border-amber-200 bg-white text-slate-800'}`}
                  />
                </div>

                <div className="rounded-xl border border-amber-200 bg-amber-50 p-3">
                  <p className="text-sm font-semibold text-slate-900">Skills in Progress</p>
                  <textarea
                    rows={3}
                    name="skills_in_progress"
                    value={formData.skills_in_progress}
                    onChange={handleChange}
                    disabled={saving || isSkillsInProgressLocked}
                    className={`mt-2 w-full rounded-xl border px-3 py-2.5 text-sm ${isSkillsInProgressLocked ? 'cursor-not-allowed border-gray-200 bg-gray-100 text-gray-500' : 'border-amber-200 bg-white text-slate-800'}`}
                  />
                </div>
              </div>
            </article>

            <article className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm sm:p-6">
              <div className="mb-3 flex items-center gap-2">
                <UserRound className="h-4 w-4 text-violet-500" />
                <h3 className="text-base font-semibold text-slate-900">AI Coach Mode</h3>
              </div>
              <div className={`grid gap-2.5 ${isDesktop ? 'grid-cols-2' : 'grid-cols-1'}`}>
                {COACH_MODES.map((mode) => {
                  const locked = isCoachModeLocked(mode.id);
                  const active = formData.coach_mode === mode.id;
                  return (
                    <button
                      key={mode.id}
                      type="button"
                      onClick={() => {
                        if (locked) {
                          setLimitErrorMessage(`Coach mode "${mode.id}" is not available on your current plan.`);
                          setShowLimitModal(true);
                          return;
                        }
                        setFormData((prev) => ({ ...prev, coach_mode: mode.id }));
                      }}
                      className={`rounded-xl border p-3 text-left ${locked ? 'cursor-not-allowed border-gray-200 bg-gray-100 text-gray-500' : active ? 'border-orange-300 bg-orange-50' : 'border-gray-200 bg-white hover:border-gray-300'}`}
                    >
                      <div className={`mb-2 inline-flex h-7 w-7 items-center justify-center rounded-lg text-xs font-semibold ${active ? 'bg-orange-500 text-white' : 'bg-gray-100 text-gray-600'}`}>{mode.symbol}</div>
                      <p className={`text-sm font-semibold ${active ? 'text-orange-700' : 'text-slate-900'}`}>{mode.title}</p>
                      <p className="mt-1 text-[11px] leading-relaxed text-gray-500">{mode.description}</p>
                      {locked ? <p className="mt-1 text-[11px] font-semibold text-gray-500">Upgrade to unlock</p> : null}
                    </button>
                  );
                })}
              </div>
            </article>

            <button
              type="button"
              onClick={saveProfile}
              disabled={saving || loading}
              className={`inline-flex items-center justify-center gap-2 rounded-xl px-6 py-3 text-sm font-semibold text-white transition ${saving ? 'bg-emerald-500' : 'bg-orange-500 hover:bg-orange-600'} disabled:cursor-not-allowed disabled:opacity-60`}
            >
              {saving ? <Check className="h-4 w-4" /> : <Save className="h-4 w-4" />}
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>

          <div>
            <article className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm sm:p-5 lg:sticky lg:top-24">
              <div className="mb-4 flex items-start justify-between gap-3">
                <div>
                  {currentPlan.badge ? <span className="inline-flex rounded-md bg-orange-500 px-2 py-0.5 text-[10px] font-semibold uppercase text-white">{currentPlan.badge}</span> : null}
                  <h3 className="mt-2 text-lg font-semibold text-slate-900">Plan capabilities</h3>
                  <p className="text-xs text-gray-500">
                    {capabilities?.plan_name || currentPlan.name}
                    {isPaidPlan && planPriceLabel ? ` | ${planPriceLabel}${cadenceLabel === 'forever' ? '' : ` ${cadenceLabel}`}` : null}
                  </p>
                  {isPaidPlan ? (
                    <>
                      <p className="mt-1 text-xs text-gray-600">
                        {capabilities?.auto_renew === false
                          ? `Access until ${accessUntilLabel || 'the end of your current billing period'}`
                          : `Renews ${renewsOnLabel || 'at the end of your current billing period'}`}
                      </p>
                      <p className="mt-0.5 text-[11px] font-semibold text-gray-600">
                        Auto-renewal: {capabilities?.auto_renew === false ? 'OFF' : 'ON'}
                      </p>
                    </>
                  ) : (
                    <p className="mt-1 text-xs text-gray-600">Free plan | upgrade anytime</p>
                  )}
                </div>
                {!isPaidPlan ? (
                  <button
                    type="button"
                    onClick={() => navigate('/pricing')}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-orange-300 bg-orange-50 px-3 py-1.5 text-xs font-semibold text-orange-700 hover:bg-orange-100"
                  >
                    <CreditCard className="h-3.5 w-3.5" />
                    Upgrade plan
                  </button>
                ) : null}
              </div>

              <div className="space-y-4">
                <div>
                  <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-gray-500">Difficulty</p>
                  <div className="flex flex-wrap gap-2">
                    {difficultyLevels.map((level) => <span key={level.name} className={`rounded-full border px-3 py-1 text-xs font-semibold ${level.available ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : 'border-gray-200 bg-gray-100 text-gray-400'}`}>{level.name}</span>)}
                  </div>
                </div>

                <div>
                  <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-gray-500">Coach modes</p>
                  <div className="flex flex-wrap gap-2">
                    {coachModeAvailability.map((mode) => <span key={mode.id} className={`rounded-full border px-3 py-1 text-xs font-semibold ${mode.available ? 'border-blue-200 bg-blue-50 text-blue-700' : 'border-gray-200 bg-gray-100 text-gray-400'}`}>{mode.name}</span>)}
                  </div>
                </div>

                <div>
                  <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-gray-500">Interview topics</p>
                  <div className="flex flex-wrap gap-2">
                    {topicCapabilities.map((topic) => <span key={topic.key} className={`rounded-full border px-3 py-1 text-xs font-semibold ${topic.available ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : 'border-gray-200 bg-gray-100 text-gray-400'}`}>{topic.label}</span>)}
                  </div>
                </div>

                <div>
                  <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-gray-500">Report output depth</p>
                  <div className="flex flex-wrap gap-2">
                    {SUMMARY_DEPTH_LEVELS.map((level) => <span key={level} className={`rounded-full border px-3 py-1 text-xs font-semibold ${summaryLevel === level ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : 'border-gray-200 bg-gray-100 text-gray-500'}`}>{level.charAt(0).toUpperCase() + level.slice(1)}</span>)}
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-2">
                  <div className="rounded-lg border border-gray-200 bg-gray-50 p-2 text-center">
                    <p className="text-[10px] text-gray-500">BARS</p>
                    <p className={`text-xs font-semibold ${capabilities?.allow_bars_scoring ? 'text-emerald-700' : 'text-gray-500'}`}>{capabilities?.allow_bars_scoring ? 'Available' : 'Locked'}</p>
                  </div>
                  <div className="rounded-lg border border-gray-200 bg-gray-50 p-2 text-center">
                    <p className="text-[10px] text-gray-500">Top skills</p>
                    <p className={`text-xs font-semibold ${capabilities?.allow_top_skills ? 'text-emerald-700' : 'text-gray-500'}`}>{capabilities?.allow_top_skills ? 'Available' : 'Locked'}</p>
                  </div>
                  <div className="rounded-lg border border-gray-200 bg-gray-50 p-2 text-center">
                    <p className="text-[10px] text-gray-500">Skills progress</p>
                    <p className={`text-xs font-semibold ${capabilities?.allow_skills_in_progress ? 'text-emerald-700' : 'text-gray-500'}`}>{capabilities?.allow_skills_in_progress ? 'Available' : 'Locked'}</p>
                  </div>
                </div>

                <div className="rounded-xl border border-gray-200 bg-white p-3">
                  <p className="text-sm font-semibold text-slate-900">Included features</p>
                  <ul className="mt-2 space-y-1">
                    {currentPlan.features.map((feature) => <li key={feature} className="text-xs text-gray-600">- {feature}</li>)}
                  </ul>
                </div>

                {isPaidPlan ? (
                  <div className="border-t border-gray-200 pt-3">
                    {capabilities?.auto_renew === false ? (
                      <p className="text-xs font-medium text-gray-500">
                        Auto-renewal is already disabled.
                      </p>
                    ) : (
                      <button
                        type="button"
                        onClick={handleCancelAutoRenewal}
                        disabled={cancellingRenewal}
                        className="text-xs font-semibold text-gray-600 hover:text-red-600 disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        {cancellingRenewal ? 'Opening portal…' : 'Manage billing ->'}
                      </button>
                    )}
                  </div>
                ) : null}
              </div>
            </article>
          </div>
        </section>

        <section className="mx-auto mt-6 max-w-3xl px-4">
          <PrivacyDataSection />
        </section>
      </main>

      <Footer />

      <SubscriptionLimitModal
        isOpen={showLimitModal}
        onClose={() => setShowLimitModal(false)}
        title="Feature Locked"
        message={limitErrorMessage || 'This feature is not available on your current plan.'}
        ctaLabel="See Plans"
        onCta={() => {
          setShowLimitModal(false);
          navigate('/pricing');
        }}
      />
    </div>
  );
}
